import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Space,
  Select,
  Input,
  Row,
  Col,
  Tag,
  Statistic,
  Button,
  message,
  Table,
  Tooltip,
  Modal,
  Upload,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  HomeOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  EyeOutlined,
  CarFilled,
} from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import useAcademicStudents from "../../../hooks/academics/useAcademicStudents";
import StudentDetailDrawer from "../../../components/academics/students/StudentDetailDrawer";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useStore } from "../../../store/values-store";

const { Option } = Select;

/**
 * Student Records Page
 * Displays all students with comprehensive details, filters, and search
 */
const StudentsManagement = () => {
  const navigate = useNavigate();
  const myRoles =
    useStore((state) => state.myRoles) ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const isSystemAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");
  const {
    students,
    selectedStudent,
    loading,
    detailLoading,
    pagination,
    summary,
    fetchStudents,
    fetchStudentDetail,
    clearSelectedStudent,
  } = useAcademicStudents();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    class_id: null,
    stream_id: null,
    day_boarding: null,
    acadyr: null,
    has_transport: null,
    tuition_status: null,
  });

  // Debounce timer
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const isFirstMount = useRef(true);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Bulk upload / template state
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);

  // Bulk transport booking state
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingRoutes, setBookingRoutes] = useState([]);
  const [bookingTransportTypes, setBookingTransportTypes] = useState([]);
  const [bookingRouteId, setBookingRouteId] = useState(null);
  const [bookingTransportTypeId, setBookingTransportTypeId] = useState(null);
  const [bookingStudentIds, setBookingStudentIds] = useState([]);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [eligibleStudentsLoading, setEligibleStudentsLoading] = useState(false);

  // Check if any filter is active
  const hasActiveFilters =
    filters.search ||
    filters.class_id ||
    filters.stream_id ||
    filters.day_boarding ||
    filters.acadyr ||
    filters.has_transport !== null ||
    filters.tuition_status;

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      search: "",
      class_id: null,
      stream_id: null,
      day_boarding: null,
      acadyr: null,
      has_transport: null,
      tuition_status: null,
    });
  };

  /**
   * Fetch classes
   */
  const fetchClasses = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (response.ok) {
        const classesData = Array.isArray(response.data) ? response.data : [];
        setClasses(classesData);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    }
  }, [navigate]);

  /**
   * Fetch streams
   */
  const fetchStreams = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.stream,
        null,
        navigate,
      );
      if (response.ok) {
        const streamsData = Array.isArray(response.data) ? response.data : [];
        setStreams(streamsData);
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
      setStreams([]);
    }
  }, [navigate]);

  /**
   * Initial data load
   */
  useEffect(() => {
    fetchStudents({ page: 1 });
    fetchClasses();
    fetchStreams();
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Build query filters object
   */
  const buildQueryFilters = useCallback(() => {
    const queryFilters = {};

    if (filters.search) {
      queryFilters.search = filters.search;
    }
    if (filters.class_id) {
      queryFilters.class_id = filters.class_id;
    }
    if (filters.stream_id) {
      queryFilters.stream_id = filters.stream_id;
    }
    if (filters.day_boarding) {
      queryFilters.day_boarding = filters.day_boarding;
    }
    if (filters.acadyr) {
      queryFilters.acadyr = filters.acadyr;
    }
    if (filters.has_transport !== null) {
      queryFilters.has_transport = filters.has_transport;
    }
    if (filters.tuition_status) {
      queryFilters.tuition_status = filters.tuition_status;
    }

    return queryFilters;
  }, [filters]);

  /**
   * Handle filter changes with debounce
   */
  useEffect(() => {
    // Skip initial mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      const queryFilters = buildQueryFilters();
      fetchStudents({ filters: queryFilters, page: 1 });
    }, 300);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /**
   * Handle view details - open drawer
   */
  const handleViewDetails = async (student) => {
    setDrawerVisible(true);
    await fetchStudentDetail(student.id || student.spc);
  };

  /**
   * Close drawer
   */
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    clearSelectedStudent();
  };

  /**
   * Handle pagination change
   */
  const handlePaginationChange = (page, pageSize) => {
    const queryFilters = buildQueryFilters();
    fetchStudents({
      page,
      pageSize,
      filters: queryFilters,
    });
  };

  /**
   * Export students data to Excel with detailed fee breakdown.
   * Fetches ALL pages matching the current filters so the export is never
   * limited to just the page currently shown in the table.
   */
  const handleExportToExcel = async () => {
    if (pagination.total === 0) {
      message.warning("No data to export");
      return;
    }

    setExporting(true);
    message.loading({
      content: "Fetching all students…",
      key: "export",
      duration: 0,
    });

    let allStudents = [];
    try {
      // Use a large page_size to minimise the number of round-trips.
      const PAGE_SIZE = 500;
      const queryFilters = buildQueryFilters();
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const queryObj = { page, page_size: PAGE_SIZE, ...queryFilters };
        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.studentSearch}?${queryParams}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (!response?.ok) {
          message.error({
            content: "Failed to fetch student data",
            key: "export",
          });
          return;
        }

        const data = response.data || response;
        const results = data.results || [];
        allStudents = [...allStudents, ...results];
        hasMore = !!data.next;
        page++;
      }

      message.loading({
        content: `Preparing export for ${allStudents.length} students…`,
        key: "export",
        duration: 0,
      });

      const exportData = allStudents.map((student) => {
        const latestTransport = getLatestTransportBooking(student);

        // Get parent info with fallback to parentAccount
        const parentName =
          student.parent?.fullName ||
          student.parentAccount?.fullName ||
          student.parentAccount?.email ||
          "-";
        const parentPhone =
          student.parent?.phone || student.parentAccount?.phone || "-";
        const parentEmail =
          student.parent?.email || student.parentAccount?.email || "-";

        // Get financial summary values
        const financialSummary = student.financialSummary || {};
        const tuitionFees = financialSummary.tuitionFees || {};
        const customFees = financialSummary.customFees || {};
        const botFees = financialSummary.botFees || {};
        const transportFees = financialSummary.transportFees || {};
        const grandTotal = financialSummary.grandTotal || {};

        return {
          "Student Name": student.fullName,
          "SPC Code": student.spc || "-",
          Class: student.class?.name || "-",
          Stream: student.stream?.name || "-",
          "Day/Boarding": student.dayBoarding || "-",
          House: student.boarding?.house?.name || "Unassigned",
          Dormitory:
            student.boarding?.dormitory?.name ||
            (student.dayBoarding === "Day" ? "N/A" : "Unassigned"),
          "Academic Year": student.currentAcadyr || "-",
          "Parent Name": parentName,
          "Parent Relationship": student.parent?.relationship || "-",
          "Parent Phone": parentPhone,
          "Parent Email": parentEmail,
          "Emergency Name": student.emergencySameAsParent
            ? "Same as Parent"
            : student.emergencyContact?.name || "-",
          "Emergency Phone": student.emergencySameAsParent
            ? "Same as Parent"
            : student.emergencyContact?.phone || "-",
          "Uses Transport":
            student.dayBoarding === "Day"
              ? hasTransport(student)
                ? "Yes"
                : "No"
              : "N/A",
          "Transport Type": latestTransport?.transportType || "-",
          "Transport Route": latestTransport?.route || "-",
          // Tuition Fees Breakdown
          "Tuition Due": parseFloat(tuitionFees.totalDue || 0),
          "Tuition Paid": parseFloat(tuitionFees.totalPaid || 0),
          "Tuition Balance": parseFloat(tuitionFees.balance || 0),
          "Tuition Cleared": tuitionFees.cleared ? "Yes" : "No",
          // Custom Fees Breakdown
          "Custom Fees Due": parseFloat(customFees.totalDue || 0),
          "Custom Fees Paid": parseFloat(customFees.totalPaid || 0),
          "Custom Fees Balance": parseFloat(customFees.balance || 0),
          "Custom Fees Cleared": customFees.cleared ? "Yes" : "No",
          // BOT Fees Breakdown
          "BOT Fees Due": parseFloat(botFees.totalDue || 0),
          "BOT Fees Paid": parseFloat(botFees.totalPaid || 0),
          "BOT Fees Balance": parseFloat(botFees.balance || 0),
          "BOT Fees Cleared": botFees.cleared ? "Yes" : "No",
          // Transport Fees Breakdown
          "Transport Fees Due": parseFloat(transportFees.totalDue || 0),
          "Transport Fees Paid": parseFloat(transportFees.totalPaid || 0),
          "Transport Fees Balance": parseFloat(transportFees.balance || 0),
          "Transport Fees Cleared": transportFees.cleared ? "Yes" : "No",
          // Grand Total
          "Grand Total Due": parseFloat(grandTotal.totalDue || 0),
          "Grand Total Paid": parseFloat(grandTotal.totalPaid || 0),
          "Grand Total Balance": parseFloat(grandTotal.balance || 0),
          "All Fees Cleared": financialSummary.allCleared ? "Yes" : "No",
        };
      });

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Student Records with all data
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
        wch: Math.max(key.length + 2, 12),
      }));
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Student Records");

      // Sheet 2: Financial Summary Only (cleaner view for finance team)
      const financeSummaryData = allStudents.map((student) => {
        const fs = student.financialSummary || {};
        return {
          "Student Name": student.fullName,
          "SPC Code": student.spc || "-",
          Class: student.class?.name || "-",
          "Day/Boarding": student.dayBoarding || "-",
          House: student.boarding?.house?.name || "Unassigned",
          Dormitory:
            student.boarding?.dormitory?.name ||
            (student.dayBoarding === "Day" ? "N/A" : "Unassigned"),
          "Tuition Balance": parseFloat(fs.tuitionFees?.balance || 0),
          "Custom Fees Balance": parseFloat(fs.customFees?.balance || 0),
          "BOT Fees Balance": parseFloat(fs.botFees?.balance || 0),
          "Transport Balance": parseFloat(fs.transportFees?.balance || 0),
          "Total Balance": parseFloat(fs.grandTotal?.balance || 0),
          Status: fs.allCleared ? "CLEARED" : "PENDING",
        };
      });

      const ws2 = XLSX.utils.json_to_sheet(financeSummaryData);
      const colWidths2 = Object.keys(financeSummaryData[0] || {}).map(
        (key) => ({
          wch: Math.max(key.length + 2, 15),
        }),
      );
      ws2["!cols"] = colWidths2;

      XLSX.utils.book_append_sheet(wb, ws2, "Financial Summary");

      // Generate filename with timestamp
      const fileName = `student_records_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success({
        content: `Exported ${allStudents.length} student(s) to 2 sheets`,
        key: "export",
      });
    } catch (error) {
      console.error("Export error:", error);
      message.error({ content: "Failed to export data", key: "export" });
    } finally {
      setExporting(false);
    }
  };

  /**
   * Download enrollment template (CSV or Excel)
   */
  const handleDownloadTemplate = async () => {
    setTemplateDownloading(true);
    const token = Utilities.getCookie("access");
    try {
      const response = await fetch(
        `${domainSettings.urls.enrollment.enrollmentTemplate}/excel`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) {
        Utilities.showNotification(undefined, "Failed to download template");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enrollment_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      Utilities.showNotification(undefined, "Failed to download template");
    }
    setTemplateDownloading(false);
  };

  /**
   * Bulk upload student enrollment file
   */
  const handleBulkUpload = async () => {
    if (!uploadFileList.length) {
      Utilities.showNotification(undefined, "Please select a file to upload");
      return;
    }
    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    const formData = new FormData();
    formData.append("file", file);
    setBulkUploadLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      domainSettings.urls.enrollment.enrollmentBulkUpload,
      formData,
      navigate,
    );
    setBulkUploadLoading(false);
    if (res?.ok) {
      const importData = res?.data?.data;
      const errors = importData?.errors || [];
      const skippedRows = importData?.skipped || [];
      const successCount = importData?.success ?? 0;
      const failedCount = importData?.failed ?? 0;
      const skippedCount = importData?.num_skipped_rows ?? skippedRows.length;

      if (failedCount > 0 || skippedCount > 0) {
        Utilities.showNotification(
          "Import Completed",
          `${successCount} imported, ${failedCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped` : ""}`,
          "text-warning",
        );
      } else {
        Utilities.showNotification(
          "Success",
          res?.data?.message || "Students imported successfully",
          "text-success",
        );
      }

      errors.forEach((err) => {
        const description =
          typeof err === "string"
            ? err
            : err.error || err.reason || err.message || JSON.stringify(err);
        const identifier =
          err.spc || err.name || err.email || err.identifier || "";
        Utilities.showNotification(
          identifier ? `Failed: ${identifier}` : "Record Failed",
          description,
          "text-danger",
        );
      });

      skippedRows.forEach((skipped) => {
        const description =
          typeof skipped === "string"
            ? skipped
            : skipped.error ||
              skipped.reason ||
              skipped.message ||
              JSON.stringify(skipped);
        Utilities.showNotification("Skipped", description, "text-warning");
      });

      setBulkUploadModalVisible(false);
      setUploadFileList([]);
      fetchStudents({ page: 1 });
    } else {
      Utilities.showNotification(
        undefined,
        res?.msg || "Failed to import students",
      );
    }
  };

  /**
   * Open bulk transport booking modal and fetch eligible students (no active booking)
   */
  const openBookingModal = useCallback(async () => {
    setBookingStudentIds([]);
    setBookingRouteId(null);
    setBookingTransportTypeId(null);
    setBookingModalVisible(true);
    setEligibleStudentsLoading(true);

    const [routesRes, typesRes] = await Promise.all([
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.transport.route,
        null,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.transport.transporttype,
        null,
        navigate,
      ),
    ]);
    if (routesRes?.ok)
      setBookingRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
    if (typesRes?.ok)
      setBookingTransportTypes(
        Array.isArray(typesRes.data) ? typesRes.data : [],
      );

    let allStudents = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const res = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.academics.studentSearch}?has_transport=false&page_size=100&page=${page}`,
        null,
        navigate,
      );
      if (res?.ok) {
        const data = res.data || res;
        const results = data.results || (Array.isArray(data) ? data : []);
        allStudents = [...allStudents, ...results];
        hasMore = !!data.next;
        page++;
      } else {
        hasMore = false;
      }
    }
    setEligibleStudents(allStudents);
    setEligibleStudentsLoading(false);
  }, [navigate]);

  /**
   * Handle bulk transport booking submission
   */
  const handleBulkBooking = async () => {
    if (!bookingStudentIds.length) {
      message.warning("Please select at least one student");
      return;
    }
    if (!bookingRouteId) {
      message.warning("Please select a route");
      return;
    }
    if (!bookingTransportTypeId) {
      message.warning("Please select a transport type");
      return;
    }

    setBookingLoading(true);
    try {
      const res = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.transport.bookBulk,
        {
          studentIDs: bookingStudentIds,
          routeID: bookingRouteId,
          transportTypeID: bookingTransportTypeId,
        },
        navigate,
      );

      if (res?.ok) {
        const data = res.data || res;
        message.success(
          data.message || `${data.bookings?.length || 0} booking(s) created`,
        );
        (data.warnings || []).forEach((w) => message.warning(w));
        setBookingModalVisible(false);
        setBookingStudentIds([]);
        fetchStudents({ page: 1 });
      } else {
        message.error(res?.msg || "Failed to create bookings");
      }
    } catch (err) {
      message.error("Error processing bulk booking");
      console.error(err);
    } finally {
      setBookingLoading(false);
    }
  };

  /**
   * Format balance for display
   */
  const formatBalance = (balance) => {
    if (balance >= 1000000) {
      return `${(balance / 1000000).toFixed(1)}M`;
    }
    if (balance >= 1000) {
      return `${(balance / 1000).toFixed(0)}K`;
    }
    return balance.toLocaleString();
  };

  const getTransportBookings = (student) =>
    Array.isArray(student?.transport) ? student.transport : [];

  const hasTransport = (student) => getTransportBookings(student).length > 0;

  const getLatestTransportBooking = (student) =>
    getTransportBookings(student)[0] || null;

  // Table columns
  const columns = [
    {
      title: "Student",
      key: "studentName",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.fullName}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {record.spc || "No SPC"}
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: "Class",
      key: "class",
      render: (_, record) => record.class?.name || "-",
      width: 120,
    },
    {
      title: "Stream",
      key: "stream",
      render: (_, record) => record.stream?.name || "-",
      width: 100,
    },
    {
      title: "Day/Boarding",
      key: "dayBoarding",
      dataIndex: "dayBoarding",
      render: (type) => (
        <Tag color={type === "Boarding" ? "purple" : "cyan"}>{type || "-"}</Tag>
      ),
      width: 100,
    },
    {
      title: "House",
      key: "house",
      render: (_, record) => {
        const houseName = record.boarding?.house?.name;
        return houseName ? (
          <Tag color="purple">{houseName}</Tag>
        ) : (
          <Tag color="warning">Unassigned</Tag>
        );
      },
      width: 130,
    },
    {
      title: "Dormitory",
      key: "dormitory",
      render: (_, record) => {
        const dormitoryName = record.boarding?.dormitory?.name;
        if (!dormitoryName && record.dayBoarding === "Day") {
          return <span style={{ color: "#999" }}>N/A</span>;
        }
        return dormitoryName ? (
          <Tag color="purple">{dormitoryName}</Tag>
        ) : (
          <Tag color="warning">Unassigned</Tag>
        );
      },
      width: 130,
    },
    {
      title: "Transport",
      key: "transport",
      render: (_, record) => {
        if (record.dayBoarding !== "Day") {
          return <span style={{ color: "#999" }}>N/A</span>;
        }

        const latestTransport = getLatestTransportBooking(record);
        if (!latestTransport) {
          return <Tag>No</Tag>;
        }

        return (
          <Tooltip title={`Route: ${latestTransport.route || "N/A"}`}>
            <Tag color="blue" icon={<CarOutlined />}>
              {latestTransport.transportType || "Yes"}
            </Tag>
          </Tooltip>
        );
      },
      width: 110,
    },
    {
      title: "Parent/Guardian",
      key: "parent",
      render: (_, record) => {
        // Try parent (from admission form) first, then parentAccount (linked user)
        if (record.parent) {
          return (
            <div>
              <div style={{ fontSize: "13px" }}>{record.parent.fullName}</div>
              <div style={{ fontSize: "11px", color: "#888" }}>
                {record.parent.relationship || ""}
              </div>
            </div>
          );
        }
        // Fallback to linked parent account
        if (record.parentAccount) {
          const displayName =
            record.parentAccount.fullName || record.parentAccount.email;
          return (
            <div>
              <div style={{ fontSize: "13px" }}>{displayName}</div>
              {record.parentAccount.fullName && record.parentAccount.email && (
                <div style={{ fontSize: "11px", color: "#888" }}>
                  {record.parentAccount.email}
                </div>
              )}
            </div>
          );
        }
        return <span style={{ color: "#999" }}>-</span>;
      },
      width: 160,
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => {
        // Try parent phone first
        if (record.parent?.phone) {
          return (
            <span style={{ fontSize: "12px" }}>{record.parent.phone}</span>
          );
        }
        // Fallback to parent account phone or email
        if (record.parentAccount) {
          if (record.parentAccount.phone) {
            return (
              <span style={{ fontSize: "12px" }}>
                {record.parentAccount.phone}
              </span>
            );
          }
          // Show email as contact if no phone
          if (record.parentAccount.email) {
            return (
              <span style={{ fontSize: "12px", color: "#1890ff" }}>
                {record.parentAccount.email}
              </span>
            );
          }
        }
        return <span style={{ color: "#999" }}>-</span>;
      },
      width: 130,
    },
    {
      title: "Emergency",
      key: "emergency",
      render: (_, record) => {
        if (record.emergencySameAsParent) {
          return <Tag color="default">Same as Parent</Tag>;
        }
        if (record.emergencyContact) {
          return (
            <Tooltip
              title={`${record.emergencyContact.name} (${record.emergencyContact.relationship || ""})`}
            >
              <span style={{ fontSize: "12px" }}>
                {record.emergencyContact.phone}
              </span>
            </Tooltip>
          );
        }
        return <span style={{ color: "#999" }}>-</span>;
      },
      width: 140,
    },
    {
      title: "Tuition",
      key: "tuitionStatus",
      render: (_, record) => {
        const cleared = record.financialSummary?.tuitionCleared;
        const balance = parseFloat(
          record.financialSummary?.grandTotal?.balance || 0,
        );

        if (cleared) {
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Cleared
            </Tag>
          );
        }
        return (
          <Tooltip title={`Balance: UGX ${balance.toLocaleString()}`}>
            <Tag color="red" icon={<CloseCircleOutlined />}>
              {formatBalance(balance)}
            </Tag>
          </Tooltip>
        );
      },
      width: 120,
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            >
              View
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Student Records | Academics</title>
      </Helmet>

      <div className="content-wrapper">
        <div className="mg-b-20">
          <h4 className="mg-b-5">Student Records</h4>
          <p className="tx-color-03 mg-b-0">
            Search for students and view their comprehensive details including
            contacts and tuition status.
          </p>
        </div>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="Total Students"
                value={summary.total}
                prefix={<TeamOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="Day Scholars"
                value={summary.day}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="Boarders"
                value={summary.boarding}
                prefix={<HomeOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="With Transport"
                value={summary.withTransport}
                prefix={<CarOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="Tuition Cleared"
                value={summary.clearedTuition}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic
                title="Pending Fees"
                value={summary.total - summary.clearedTuition}
                valueStyle={{
                  color:
                    summary.total - summary.clearedTuition > 0
                      ? "#f5222d"
                      : "#52c41a",
                }}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          {/* Search Bar */}
          <Input
            placeholder="Search by student name or SPC code..."
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            allowClear
            size="large"
            style={{ marginBottom: 16 }}
          />

          {/* Filters Section */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <FilterOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                <span style={{ fontWeight: 500, marginRight: 16 }}>
                  Filters
                </span>
                {hasActiveFilters && (
                  <Button
                    type="link"
                    size="small"
                    icon={<ClearOutlined />}
                    onClick={clearAllFilters}
                    style={{ padding: 0 }}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              {isSystemAdmin && (
                <Space>
                  <Button
                    loading={templateDownloading}
                    className="btn-info border-0"
                    onClick={handleDownloadTemplate}
                    icon={<i className="fas fa-download" />}
                  >
                    Template
                  </Button>
                  <Button
                    className="border-0"
                    style={{
                      background: "#722ed1",
                      borderColor: "#722ed1",
                      color: "#fff",
                    }}
                    onClick={() => {
                      setUploadFileList([]);
                      setBulkUploadModalVisible(true);
                    }}
                    icon={<i className="fas fa-file-upload" />}
                  >
                    Import
                  </Button>
                  <Button
                    className="border-0"
                    style={{
                      background: "#13c2c2",
                      borderColor: "#13c2c2",
                      color: "#fff",
                    }}
                    onClick={openBookingModal}
                    icon={<CarFilled />}
                  >
                    Book Transport
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportToExcel}
                    loading={exporting}
                    disabled={pagination.total === 0}
                  >
                    Export to Excel
                  </Button>
                </Space>
              )}
            </div>

            <Row gutter={[12, 12]}>
              {/* Class Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.class_id}
                  onChange={(value) =>
                    setFilters({ ...filters, class_id: value ?? null })
                  }
                >
                  {Array.isArray(classes) &&
                    classes.map((cls) => (
                      <Option key={cls.id} value={cls.id}>
                        {cls.name}
                      </Option>
                    ))}
                </Select>
              </Col>

              {/* Stream Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Stream"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.stream_id}
                  onChange={(value) =>
                    setFilters({ ...filters, stream_id: value ?? null })
                  }
                >
                  {Array.isArray(streams) &&
                    streams.map((stream) => (
                      <Option key={stream.id} value={stream.id}>
                        {stream.name}
                      </Option>
                    ))}
                </Select>
              </Col>

              {/* Day/Boarding Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Day/Boarding"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.day_boarding}
                  onChange={(value) =>
                    setFilters({ ...filters, day_boarding: value ?? null })
                  }
                >
                  <Option value="Day">Day</Option>
                  <Option value="Boarding">Boarding</Option>
                </Select>
              </Col>

              {/* Academic Year Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyr}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyr: value ?? null })
                  }
                >
                  {Array.isArray(academicYears) &&
                    academicYears.map((year) => (
                      <Option key={year.id} value={year.acadyr}>
                        {year.acadyr}
                      </Option>
                    ))}
                </Select>
              </Col>

              {/* Transport Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Transport"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.has_transport}
                  onChange={(value) =>
                    setFilters({ ...filters, has_transport: value ?? null })
                  }
                >
                  <Option value="true">Uses Transport</Option>
                  <Option value="false">No Transport</Option>
                </Select>
              </Col>

              {/* Tuition Status Filter */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Tuition Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.tuition_status}
                  onChange={(value) =>
                    setFilters({ ...filters, tuition_status: value ?? null })
                  }
                >
                  <Option value="cleared">Cleared</Option>
                  <Option value="pending">Pending</Option>
                </Select>
              </Col>
            </Row>
          </div>

          {/* Results Summary */}
          {hasActiveFilters && (
            <div style={{ marginBottom: 16 }}>
              <Tag color="blue">
                Showing {students.length} of {pagination.total} student(s)
              </Tag>
            </div>
          )}

          {/* Students Table */}
          <Table
            columns={columns}
            dataSource={students}
            rowKey={(record) => record.id}
            loading={loading}
            pagination={{
              current: pagination.current || 1,
              pageSize: pagination.pageSize || 10,
              total: pagination.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} student(s)`,
              responsive: true,
              onChange: (page, pageSize) =>
                handlePaginationChange(page, pageSize),
            }}
            scroll={{ x: 1400 }}
            size="middle"
            style={{ marginTop: 16 }}
          />
        </Card>

        {/* Student Detail Drawer */}
        <StudentDetailDrawer
          visible={drawerVisible}
          onClose={handleCloseDrawer}
          student={selectedStudent}
          loading={detailLoading}
          onUpdated={async () => {
            if (selectedStudent?.id || selectedStudent?.spc) {
              await fetchStudentDetail(
                selectedStudent.id || selectedStudent.spc,
              );
            }
            fetchStudents({
              page: pagination.current || 1,
              filters: buildQueryFilters(),
            });
          }}
        />

        {/* Bulk Import Modal */}
        <Modal
          title="Import Student Records"
          open={bulkUploadModalVisible}
          onOk={handleBulkUpload}
          onCancel={() => {
            setBulkUploadModalVisible(false);
            setUploadFileList([]);
          }}
          okText="Import"
          confirmLoading={bulkUploadLoading}
          destroyOnClose
        >
          <p className="mb-3 text-muted">
            Upload an Excel or CSV file to bulk import students. Use the{" "}
            <strong>Template</strong> button to download the correct format
            first.
          </p>
          <Upload.Dragger
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            fileList={uploadFileList}
            beforeUpload={() => false}
            onChange={({ fileList }) => setUploadFileList(fileList)}
          >
            <p className="ant-upload-drag-icon">
              <i className="fas fa-file-upload fa-2x" />
            </p>
            <p className="ant-upload-text">Click or drag file here</p>
            <p className="ant-upload-hint">
              Supported formats: .xlsx, .xls, .csv
            </p>
          </Upload.Dragger>
        </Modal>

        {/* Bulk Transport Booking Modal */}
        <Modal
          title="Book Transport"
          open={bookingModalVisible}
          onOk={handleBulkBooking}
          onCancel={() => {
            setBookingModalVisible(false);
            setBookingStudentIds([]);
          }}
          okText="Book"
          confirmLoading={bookingLoading}
          destroyOnClose
        >
          <p className="mb-3 text-muted">
            Select students without an active transport booking, then choose a
            route and transport type.
          </p>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <label className="text-muted" style={{ fontSize: 12 }}>
                  Students
                </label>
                <Space size="small">
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 12 }}
                    disabled={
                      eligibleStudentsLoading || eligibleStudents.length === 0
                    }
                    onClick={() =>
                      setBookingStudentIds(eligibleStudents.map((s) => s.id))
                    }
                  >
                    Select All ({eligibleStudents.length})
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 12 }}
                    disabled={bookingStudentIds.length === 0}
                    onClick={() => setBookingStudentIds([])}
                  >
                    Clear
                  </Button>
                </Space>
              </div>
              <Select
                mode="multiple"
                placeholder="Search and select students..."
                style={{ width: "100%" }}
                loading={eligibleStudentsLoading}
                value={bookingStudentIds}
                onChange={setBookingStudentIds}
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                options={eligibleStudents.map((s) => ({
                  label: `${s.fullName} — ${s.class?.name || ""} (${s.spc || ""})`,
                  value: s.id,
                }))}
              />
            </div>
            <div>
              <label
                className="d-block mb-1 text-muted"
                style={{ fontSize: 12 }}
              >
                Route
              </label>
              <Select
                placeholder="Select route"
                style={{ width: "100%" }}
                value={bookingRouteId}
                onChange={setBookingRouteId}
                showSearch
                optionFilterProp="children"
              >
                {bookingRoutes.map((r) => (
                  <Option key={r.id} value={r.id}>
                    {r.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <label
                className="d-block mb-1 text-muted"
                style={{ fontSize: 12 }}
              >
                Transport Type
              </label>
              <Select
                placeholder="Select transport type"
                style={{ width: "100%" }}
                value={bookingTransportTypeId}
                onChange={setBookingTransportTypeId}
                showSearch
                optionFilterProp="children"
              >
                {bookingTransportTypes.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Space>
        </Modal>
      </div>
    </>
  );
};

export default StudentsManagement;
